import "photoswipe/dist/photoswipe.css";
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import cloneDeep from "lodash/cloneDeep";
import { Gallery, Item } from "react-photoswipe-gallery";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useNavigate, useParams } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { setIsNDAAccepted, setIsCifCompletedForPACT } from "@/redux/PDP/pdpSlice";
import { getUserProfile } from "@/api/myProfile";
import { globalCmsData } from "@/redux/cms/cmsSelectors";
import { getPDPInitData } from "@/api/PDP";

import {
  fetchPDPInitialLoad,
  fetchInvestCalLoad,
  fetchPDPBidCenterData,
  fetchPDPLatestBidCenterData,
  fetchPDPBidCenterDataSuccess,
  resetPDPState,
} from "@/redux/PDP/pdpSlice";
import { setProfileInfo } from "@/redux/user/userSlice";
import {
  getPriceHistory,
  getTax,
  getCMS,
  getDisclaimer,
  getEMD,
  getInspectionContingency,
  getAds,
  getSmsWatchData,
  getNearbyProperties,
  updatePropertyViewCount,
} from "@/api/PDP";
import { isGetCodeAllowed } from '@/api/AccessCode';
import isOccupiedImg from "../../../public/assets/images/dnd.png";
import allFCEvent from '../../../public/assets/images/pdp/allFCEvent.svg';

// Regular imports
import Header from "@/components/common/header/DefaultHeader";
import MobileMenu from "@/components/common/header/MobileMenu";
import PopupSignInUp from "@/components/common/PopupSignInUp";
import ListingOne from "./ListingOne";
import MetaComponent from "@/components/common/MetaComponent";
import Loader from "../common/loader/Loader";
import ForeclosureDetails from "./BidCenter/ForeclosureDetails";
import LocalInfo from "./LocalInfo";
import AuctionFirm from "./pdpComponents/AuctonFirm";
import AuctionFirmSPS from "./pdpComponents/AuctonFirmSPS";
import PSO from "./pdpComponents/PSO";
import CaseFileDetails from "./pdpComponents/CaseFileDetails";
import ListedBy from "./pdpComponents/ListedBy";
import BuyingWithHubzu from "./pdpComponents/BuyingWithHubzu";
import NeedHelp from "./pdpComponents/NeedHelp";
import Disclaimer from "./pdpComponents/Disclaimer";
import DocumentSection from "./pdpComponents/DocumentSection";
import PropertyInfo from "./pdpComponents/PropertyInfo";
import PropertyFees from "./pdpComponents/PropertyFees";
import PropertyInfoCommercial from "./pdpComponents/PropertyInfo_Commercial";
import PropertyInfoDetails from "./pdpComponents/PropertyInfoDetails";
import Advertisment from "./pdpComponents/Advertisment";
import PdpNewAds from "./pdpComponents/PdpNewAds";
import BidCenter from "./BidCenter/BidCenter";
import InvestmentCalculator from "./pdpComponents/InvestmentCalculator";
import AlertRegister from "./pdpComponents/AlertRegister";
import GetAccessCode from "./pdpComponents/GetAccessCode/GetAccessCode";
import SteadilyBanner from "./pdpComponents/SteadilyBanner";
import BirdseyBanner from "./pdpComponents/BirdseyBanner";
import ForeclosureSales from "./pdpComponents/ForeclosureSales";
import FeaturesSection from "./pdpComponents/FeaturesSection";
import GalleryHeader from "./pdpComponents/GalleryHeader";
import PropertyTags from "./pdpComponents/PropertyTags";
import TakeTour from "../takeTour";
import DocumentSectionCommercial from "./pdpComponents/DocumentSectionCommercial";
import PropertyDescriptions from "../common/listing-details/PropertyDescriptions";
import PDPHeader from "./PDPHeader";
import NearbyProperties from "./NearbyProperties";
import ShortSales from "./pdpComponents/ShortSales";
import CwcotSales from "./pdpComponents/CwcotSales";
import AssignmentContract from "./pdpComponents/AssignmentContract";
import ContactUsAgents from "./pdpComponents/ContactUsAgents";
import Partners from "../common/Partners";
import Joyride from "react-joyride";

// Import tour steps synchronously as they're small
import { pdpSteps, tourStyles } from "../takeTour/tourSteps";
import { usePDPData } from './pdpHooks/usePDPData.js';
import './style/pdp.css';


import CriticalCSS from './CriticalCSS';
import LazyImage from './LazyImage';
import PreloadResources from './PreloadResources';
import ShimmerLoader from './ShimmerLoader';
import { trackButtonClick, trackPageMount } from "@/utils/analytics";
import { markInternalNavigation } from "@/utils/attributionCapture";
import { generatePropertyDetailSchema, injectSchema, removeSchema } from "@/utils/schema";
import OtherLinkFooter from "../common/footer/OtherLinkFooter";
import NewFooter from "../common/footer/NewFooter";
import { getPropertyURL } from "@/utils/propertyNavigation";

const PropertyDetails = React.memo(() => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Memoize propertyId extraction
  const propertyId = useMemo(() => {
    const id = params?.id;
    return id?.indexOf('-') > 0 ? id.slice(0, id.indexOf('-')) : id;
  }, [params?.id]);

  // Redux selectors with memoization

    const user = useSelector((state) => state.user);

  const pdpInitData = useSelector((state) => state.pdp.pdpInitData);
  const isPDPLoading = useSelector((state) => state.pdp.isPDPLoading);
  const isUserLoggedIn = useSelector((state) => state.user.isUserLoggedIn);
  const calculatorFields = useSelector(state => state.pdp?.investmentInitData?.response?.data_record?.calculator_fields);
  const bidCenterData = useSelector((state) => state.pdp?.bidCenterData);
  const pdpGlobalCmsData = useSelector(globalCmsData);
  const smsOptInStatus = useSelector(
    (state) => state?.user.profileInfo?.userDetails?.smsOptInStatus
  );

  // Custom hook for PDP data management
  const {
    cmsData,
    cmsLoaded,
    showGetCode,
    taxRecord,
    priceHistory,
    cmsKeyInfographic,
    disclaimer,
    emd,
    pdpAds,
    nearbyProperties,
    propertyWatchStatus,
    inspectionContigencyDesc,
    setShowGetCode,
    setPropertyWatchStatus,
  } = usePDPData(propertyId, pdpInitData);

  // Track API call status to prevent duplicates
  const [apiCallsInitiated, setApiCallsInitiated] = useState({
    bidCenter: false,
    latestBidCenter: false
  });

  const [runTour, setRunTour] = useState(false);
  const [isContentReady, setIsContentReady] = useState(false);
  const [isWrongURL, setIsWrongURL] = useState(false);
  const [propertyNotAvailable, setPropertyNotAvailable] = useState(false);

  const [showSteadilyBanner, setShowSteadilyBanner] = useState(false);
  const [showCryptoBanner, setShowCryptoBanner] = useState(false);
  const [showBirdseyeBanner, setShowBirdseyeBanner] = useState(false);




  // Set content ready when all required data is loaded
  useEffect(() => {
    if (!isPDPLoading && cmsLoaded) {

      setIsContentReady(true);
    }
  }, [isPDPLoading, cmsLoaded, pdpInitData?.propertyDetails]);

  // Set SteadilyBanner visibility once when CMS data loads
  useEffect(() => {
    if (cmsData?.pdpSteadliy?.EnableBanner === true) {
      setShowSteadilyBanner(true);
    }
  }, [cmsData?.pdpSteadliy?.EnableBanner]);

  // Set CryptoBanner visibility once when CMS data loads
  useEffect(() => {
    if (cmsData?.pdpCryptoBanner?.EnableBanner === true) {
      setShowCryptoBanner(true);
    }
  }, [cmsData?.pdpCryptoBanner?.EnableBanner]);

  // Set BirdseyeBanner visibility once when CMS data loads
  useEffect(() => {
    if (cmsData?.pdpBirdsEyeBanner?.EnableBanner === true) {
      setShowBirdseyeBanner(true);
    }
  }, [cmsData?.pdpBirdsEyeBanner?.EnableBanner]);

  // Inject schema for SEO
  useEffect(() => {
    if (pdpInitData) {
      const schemas = generatePropertyDetailSchema(pdpInitData);
      if (schemas) {
        injectSchema(schemas, 'schema-org');
      }
    }
    return () => removeSchema('schema-org');
  }, [pdpInitData]);

  // Scroll depth tracking
  useEffect(() => {
    const scrollDepthThresholds = [25, 50, 75, 100];
    const triggeredThresholds = new Set();

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercentage = Math.round((scrollTop / documentHeight) * 100);

      scrollDepthThresholds.forEach(threshold => {
        if (scrollPercentage >= threshold && !triggeredThresholds.has(threshold)) {
          triggeredThresholds.add(threshold);
          trackPageMount({
            pageName: "scroll_depth",
            eventCategory: "NEW-PDP",
            eventLabel: `${threshold}% scroll depth`,
            eventSource: `Scroll Depth - ${scrollPercentage}%`,
          });
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Memoized callbacks and computed values
  const getDetailsById = useCallback((id) => {
    const item = cmsData?.pdpBidCenter?.CAFCSaleProperties[id];
    if (!item) return null;
    const { saleDate, phoneNumber, salePrice, caseFileNumber, trusteeAddress, trusteename } = item;
    return {
      saleDate,
      phoneNumber,
      salePrice,
      caseFileNumber,
      trusteeAddress,
      trusteename
    };
  }, [cmsData?.pdpBidCenter?.CAFCSaleProperties]);

  const fullAddress = useMemo(() => {
    const addr = pdpInitData?.propertyDetails?.address;
    if (!addr) return "";
    return `${addr.streetNumber} ${addr.streetName}, ${addr.city}, ${addr.state} ${addr.zip}`;
  }, [pdpInitData?.propertyDetails?.address]);

  const isCAFCSaleProperty = useMemo(() => {
      const item = cmsData?.pdpBidCenter?.CAFCSaleProperties[propertyId];
        if (!item) return null;
    return pdpInitData?.eventCenterDetail !== null &&
      pdpInitData?.fcOnlineFlag !== "Y" &&
      pdpInitData?.propertyDetails?.address?.state === "CA" &&
      pdpInitData?.propertyDetails?.commonAttributes?.propCategory === "FORECLOSURE";
  }, [pdpInitData?.eventCenterDetail, pdpInitData?.fcOnlineFlag, pdpInitData?.propertyDetails?.address?.state, pdpInitData?.propertyDetails?.commonAttributes?.propCategory,cmsData?.pdpBidCenter?.CAFCSaleProperties]);

  const caFcSalePropertyDetails = useMemo(() =>
    getDetailsById(propertyId) || null,
    [getDetailsById, propertyId]
  );

  const metadata = {
  title: fullAddress,
  description: "Real Estate ",
  };

  // Consolidated effect for bid center data loading
  useEffect(() => {
    if (!pdpInitData?.propertyDetails) return;

    const listingId = pdpInitData?.propertyDetails?.listingId;
    const propCategory = pdpInitData.propertyDetails.commonAttributes?.propCategory;


    if (listingId && !apiCallsInitiated.bidCenter) {

      dispatch(fetchPDPBidCenterData(listingId));
      setApiCallsInitiated(prev => ({ ...prev, bidCenter: true }));
    }

    if (listingId && !apiCallsInitiated.latestBidCenter) {
      dispatch(fetchPDPLatestBidCenterData(listingId));
      setApiCallsInitiated(prev => ({ ...prev, latestBidCenter: true }));
    }
  }, [pdpInitData?.propertyDetails?.listingId]);

  // Update URL based on property category
  useEffect(() => {
    if (!pdpInitData?.propertyDetails) return;
    if (pdpInitData?.propertyDetails?.propertyId !== propertyId) return;

    const propCategory = pdpInitData?.propertyDetails?.commonAttributes?.propCategory;
    const status = pdpInitData?.propertyDetails?.commonAttributes?.status;

    if (propCategory && window.location.pathname.includes('property')) {
      const newURL = getPropertyURL(window.location.pathname, propCategory, status);
      // For commercial properties, use navigate so React Router renders CommercialPropertyDetails
      if (propCategory === "COMMERCIAL") {
        navigate(newURL, { replace: true });
        return;
      }
      window.history.replaceState(null, '', newURL);
    }
    // if (propCategory && window.location.pathname.includes('preview')) {
    //   window.history.replaceState(null, '', window.location.pathname.replace('/preview', ''));
    // }
    // if (propCategory && !(window.location.pathname.includes('preview')) && (status === 'PACT' || status === 'PACT_NON_COMMERCIAL')) {
    //   updatePreviewURL(window.location.pathname, propCategory);
    // }
    const correctURL = getPropertyURL(`/property/${propertyId}`, propCategory, status);
    if (!window.location.pathname.includes(correctURL)) {
      setIsWrongURL(true);
    }
  }, [pdpInitData?.propertyDetails?.commonAttributes?.propCategory, propertyId]);

  // Navigation effect for property status changes
  useEffect(() => {
    if (pdpInitData?.propertyDetails?.propertyId !== propertyId) return;

    const status = pdpInitData?.propertyDetails?.commonAttributes?.status;
    const propertyTag = pdpInitData?.propertySpecialDetails?.propertyTag;
    const realbidStatus = pdpInitData?.propertyDetails?.detailedAttributes?.realbidPropertyStatus;
    const stateLandingUrl = pdpInitData?.propertyDetails?.address?.stateLandingUrl;

    if (status === 'SLD' || status === 'DELETED') {
      navigate(stateLandingUrl);
    } else if (status === 'REVIEW_PENDING' ||status === 'INCOMPLETE'  ||
               (propertyTag === 'UNDER_CONTRACT' &&
                (realbidStatus === 'REOS_RC' || realbidStatus === 'REOS_LC'))) {
      navigate(stateLandingUrl);
    }
  }, [pdpInitData?.propertyDetails?.commonAttributes?.status, pdpInitData?.propertyDetails?.propertyId, propertyId, navigate]);

  // Reset API call tracking when propertyId changes
  useEffect(() => {
    setApiCallsInitiated({ bidCenter: false, latestBidCenter: false });
    dispatch(resetPDPState());
  }, [propertyId]);

  // Refresh PDP data when login state changes
  useEffect(() => {
    if (propertyId) {

       onSuccessfullLogin();
    }
  }, [user?.isUserLoggedIn]);

  const onSuccessfullLogin = async () => {
       dispatch(fetchPDPBidCenterData(pdpInitData?.propertyDetails?.listingId));
      // dispatch(fetchPDPLatestBidCenterData(pdpInitData.propertyDetails.listingId));
     const details = await getPDPInitData(propertyId);

       if (details) {
         dispatch(setIsNDAAccepted(details?.ndaCommercialAccepted));
         dispatch(setIsCifCompletedForPACT(details?.cifCompleted));
    }
  }

  const handleForeClosure = useCallback(() => {
    trackButtonClick({
      eventName: "see_all_foreclosure_events",
      eventCategory: "NEW-PDP",
      eventSource: "on see all foreclosure events click",
      eventLabel: `PropertyId: ${pdpInitData?.propertyDetails?.propertyId}`
    })
    window.open("/foreclosure/eventCalendar", "_blank", "noopener noreferrer");
  }, []);

  const handleForeClosureEvents = useCallback(() => {
    const eventId = pdpInitData?.eventCenterDetail?.event?.id;
    const location = pdpInitData?.eventCenterDetail?.event?.venueDetails?.state?.toLowerCase();
    const url = `/foreclosure/${location}/auctions?eventId=${eventId}`;

   trackButtonClick({
      eventName: "foreclosure_event_details_click",
      eventCategory: "NEW-PDP",
      eventSource: "Foreclosure Event Link",
      eventLabel: `PropertyId: ${pdpInitData?.propertyDetails?.propertyId}`
    })

    window.open(url, "_blank");
  }, [pdpInitData?.eventCenterDetail?.event]);

  const isListingAgentPresent = useCallback((listingAgentDetail) => {
    if (!listingAgentDetail) return false;

    const clonedDetails = cloneDeep(listingAgentDetail);
    const flatAuctioneerDetail = { ...clonedDetails.auctioneerDetail };
    delete clonedDetails.auctioneerDetail;

    const flatListingAgentDetail = {
      ...clonedDetails,
      ...flatAuctioneerDetail,
    };

    return Object.values(flatListingAgentDetail).some(value => value && value !== "-");
  }, []);

  const loadImageItem = useCallback((imgData) => {
    if (!imgData?.pdpBigImage) return null;

    const imageUrl = "https:" + imgData.pdpBigImage;

    return (
      <Item
        original={imageUrl}
        thumbnail={imageUrl}
        width={752}
        height={450}
      >
        {({ ref, open }) => (
          <div role="button" ref={ref} onClick={open}>
            <img
              className="img-fluid w100 cover lds-1"
              src={imageUrl}
              alt="Property image"
            />
          </div>
        )}
      </Item>
    );
  }, []);
  const sectionRef = useRef(null);



  const PropertyDetailComp = useCallback(() => {
    if (!pdpInitData?.propertyDetails) return null;

    const { offerCenterDetail } = pdpInitData;
    const { address, commonAttributes, detailedAttributes } = pdpInitData.propertyDetails || {};
    const hasHnb = pdpInitData?.propertySpecialDetails?.hasHnb;
    const hnbDeadlnDt = pdpInitData?.propertySpecialDetails?.hnbDeadlnDt;

    const {
      pdpBidCenter,
      pdpListedBy,
      pdpPromotionBanner,
      pdpPropertyFees,
      pdpDocuments,
      pdpRentRange,
      pdpUpcomingView,
      pdpForeclosureCounty,
      pdpDisclaimer,
      pdpInvestmentCalculator,
    } = cmsData || {};

    const {
      listingType,
      listingStatus,
      financialType,
      businessListingId,
      provisionalSaleDuration,
    } = pdpInitData.listingDetail || {};


    const { status, propCategory } = commonAttributes || {};
    const { ndaCommercialAccepted } = pdpInitData;
    const { isAllowedPreSale, numberOfViews } = detailedAttributes || {};

    // Memoize status flags to prevent recalculation
    const statusFlags = useMemo(() => {
      const isAuctionEventEnded = status === "AUCTION_EVENT_ENDED";
      const isPreview = isAllowedPreSale;
      const isUnderReviewAuction = status === "REVIEW" && listingType === "AUCN";
      const isUnderContractAuction = status === "SPND" && listingType === "AUCN";
      const isForeclosure = propCategory === "FORECLOSURE";
      const isActiveAuction = status === "ACTV" && listingType === "AUCN";
      const isShortSaleUnderContractAuction = propCategory === "SHRT_SALE" && status === "SPND" && listingType === "AUCN";
      const isNPL = propCategory === "NPL";
      const isShortSaleAuctionEnded = propCategory === "SHRT_SALE" && isAuctionEventEnded;
      const isShortSaleUnderContractTraditional = propCategory === "SHRT_SALE" && status === "SPND" && listingType === "TRNL";
      const isUnderContractTraditional = offerCenterDetail?.offerTypes?.indexOf("PLACE_BACKUP_OFFER") > -1;
      const isActive = status === "ACTV";
      const isTraditional = listingType === "TRNL";
      const isUpcoming = status === "WFA" || status === "PIFA";
      const isUnderReview = status === "REVIEW";
      const isScheduled = status === "SCHEDULED";
      const isPostponed = status === "POSTPONED";
      const isAuctionEventCancelled = status === "WLST";
      const isClearedToSell = status === "CLEAR_TO_SALE";
      const isShortSaleUnderReview = propCategory === "SHRT_SALE" && status === "REVIEW" && listingType === "AUCN";
      const isAuctionEndedFCOnline = status === "SPND" && pdpInitData?.fcOnlineFlag === "Y";
      const isHNBTraditional = isTraditional && hasHnb === "Y";

      return {
        isAuctionEventEnded, isPreview, isUnderReviewAuction, isUnderContractAuction,
        isForeclosure, isActiveAuction, isShortSaleUnderContractAuction, isNPL,
        isShortSaleAuctionEnded, isShortSaleUnderContractTraditional, isUnderContractTraditional,
        isActive, isTraditional, isUpcoming, isUnderReview, isScheduled, isPostponed,
        isAuctionEventCancelled, isClearedToSell, isShortSaleUnderReview, isAuctionEndedFCOnline,
        isHNBTraditional
      };
    }, [status, propCategory, listingType, isAllowedPreSale, hasHnb, offerCenterDetail?.offerTypes, pdpInitData?.fcOnlineFlag]);

    const {
      isAuctionEventEnded, isPreview, isUnderReviewAuction, isUnderContractAuction,
      isForeclosure, isActiveAuction, isShortSaleUnderContractAuction, isNPL,
      isShortSaleAuctionEnded, isShortSaleUnderContractTraditional, isUnderContractTraditional,
      isActive, isTraditional, isUpcoming, isUnderReview, isScheduled, isPostponed,
      isAuctionEventCancelled, isClearedToSell, isShortSaleUnderReview, isAuctionEndedFCOnline,
      isHNBTraditional
    } = statusFlags;

    const isOccupied = commonAttributes?.isOccupied &&
      cmsKeyInfographic?.pdpStaticContent?.[propCategory]?.conditionBased?.isOccupied?.staticContentHead;

    let offerType = null;
    let isWatchProperty = false;
    if (
      offerCenterDetail?.offerTypes &&
      offerCenterDetail.offerTypes?.length > 0
    ) {
      const offerTypes = offerCenterDetail.offerTypes;
      if (offerTypes.indexOf("PLACE_OFFER") > -1) {
        offerType = "PLACE_OFFER";
      } else if (offerTypes.indexOf("PLACE_BACKUP_OFFER") > -1) {
        offerType = "PLACE_BACKUP_OFFER";
      } else if (offerTypes.indexOf("WATCH_PROPERTY") > -1) {
        offerType = "WATCH_PROPERTY";
        isWatchProperty = true;
      }
    }

    const mapCoListingAgentDetails = (coListingAgentDetail) => {
      return {
        listingAgentEmailId: coListingAgentDetail.email,
        listingAgentAddress1: coListingAgentDetail.coListingAgentAddress1,
        listingAgentAddress2: coListingAgentDetail.coListingAgentAddress2,
        listingAgentCity: coListingAgentDetail.coListingAgentCity,
        listingAgentState: coListingAgentDetail.state,
        listingAgentZip: coListingAgentDetail.coListingAgentZip,
        listingAgentPhone: coListingAgentDetail.laPhone,
        listingAgentFirstName: coListingAgentDetail.laName,
        listingAgentLastName: coListingAgentDetail.laLastName,
        listingAgentLicenseNumber: coListingAgentDetail.licenseNumber,
        agency: coListingAgentDetail.agencyName,
      };
    };
    const showStatus =
      isUnderReview ||
      isShortSaleUnderContractTraditional ||
      isPreview ||
      isUnderContractAuction ||
      isUnderContractTraditional ||
      isHNBTraditional;
    const hasAuctionFirm =
      pdpInitData?.listingAgentDetail?.auctioneerDetail?.auctioneerLcnsTxtOne ||
      pdpInitData?.listingAgentDetail?.auctioneerDetail?.auctioneerLcnsNumTwo ||
      pdpInitData?.listingAgentDetail?.auctioneerDetail?.auctioneerLcnsNumThree;

    const hasPSO =
      pdpInitData?.privateSellingOfficerDto !== null;


    const hasCaseFileDetails =
      pdpInitData?.caseFileDetail?.caseFileNumber ||
      pdpInitData?.caseFileDetail?.phoneNumber ||
      pdpInitData?.caseFileDetail?.trustName;

    const statusPayload = {
      isActive,
      isUnderReview,
      isTraditional,
      isPreview,
      isUpcoming,
      isUnderReviewAuction,
      isUnderContractAuction,
      isForeclosure,
      isUnderContractTraditional,
      isHNBTraditional,
      isScheduled,
      isShortSaleUnderContractAuction,
      isShortSaleUnderContractTraditional,
      isActiveAuction,
      hasHnb,
      isPostponed,
      isAuctionEventEnded,
      hnbDeadlnDt,
      isClearedToSell,
      isShortSaleUnderReview,
      isAuctionEndedFCOnline,
      isAuctionEventCancelled,
      isNPL,
    };

    const shouldHideAccessCode = (
      (commonAttributes?.isOccupied) ||
      (isForeclosure === true) ||
      (pdpInitData?.propertySpecialDetails?.shortSaleFlag === "Y") ||
      (pdpInitData?.propertyDetails?.detailedAttributes?.isAllowedPreSale) ||
      !(pdpInitData?.propertySpecialDetails?.pdpEnabledFeatures?.includes("ACCESS_CODE"))
    );





    const isNotSC = commonAttributes?.propCategory?.toLowerCase() !== "sc";

    const shouldShowFees =
    (isForeclosure !== true &&
    listingType === "AUCN" &&
    (commonAttributes?.propCategory?.toLowerCase() !== "sc" || commonAttributes?.propCategory !== ""))
   ||
    (isForeclosure !== true &&
    listingType !== ""
  );


    const shouldHideDiv = (
      commonAttributes?.status === "SPND" ||
      (commonAttributes?.status === "REVIEW" &&
        pdpInitData?.propertySpecialDetails?.lockBoxCode &&
        pdpInitData.propertySpecialDetails.lockBoxCode !== "-")
    );

  const user = useSelector((state) => state.user?.user);

    const renderGetAccessCode = () => {
      return (
        <>


          {!shouldHideAccessCode && isNotSC && (


            <GetAccessCode propertyData={pdpInitData?.propertyDetails} />
          )
          }


        </>
      );
    }

    return (
      <div className="pdp-div">
          <PDPHeader
          taxRecord={taxRecord}
          priceHistory={priceHistory}
          statusPayload={statusPayload}
          pdpBidCenter={pdpBidCenter}
          listingType={listingType}
          status={status}
          offerType={offerType}
          offerCenterDetail={offerCenterDetail}
          financialType={financialType}
          pdpPropertyFees={pdpPropertyFees}
          isWatchProperty={isWatchProperty}
          propertyDetails={pdpInitData?.propertyDetails}
          isForeclosure={isForeclosure}
          hidePriceHistory={cmsData?.pdpLocalInfo?.hidePriceHistory}
          hidePropertyTaxes={cmsData?.pdpLocalInfo?.hidePropertyTaxes}
          showInvestmentCal={calculatorFields?.listingprice &&
            calculatorFields?.listingprice !== "-" &&
            calculatorFields?.rent_high_mo &&
            calculatorFields?.rent_low_mo &&
            calculatorFields?.vacancy_rate &&
            calculatorFields?.rent_med_mo &&
            cmsData?.pdpInvestmentCalculator?.enable === "true" ? true : false}
        />

        <section className="our-agent-single pb10 mt110 pdp-container container mobile-pdp-resp" style={{paddingTop:60}}>
          <div className="pdp-container container pl0 pr0">
            <div className="row g-4">
              <div className="pdpLeft pr20 prmb0">
                <div>
                  <div id="Overview">
                      <GalleryHeader
                      cmsData={cmsData}
                      isForeclosure={isForeclosure}
                      pdpGlobalCmsData={pdpGlobalCmsData}
                      statusPayload={statusPayload}
                      listingType={listingType}
                      status={status}
                      offerType={offerType}
                      offerCenterDetail={offerCenterDetail}
                      isFcOnlineFlag={pdpInitData?.fcOnlineFlag}
                      pdpData={pdpInitData}
                      financialType={financialType}
                      pdpPropertyFees={pdpPropertyFees}
                      isWatchProperty={isWatchProperty}
                      cmsKeyInfographic={cmsKeyInfographic}
                      />
                    <div className="row mt-4">
                      <div className="col-md-12 col-lg-12 col-sm-12 pl0 pr5">
                          <ListingOne property={pdpInitData?.propertyDetails} />
                      </div>
                      {
                        isPreview &&

                        <div className="pb50 mt0">
                          <img className="img-whp w-100 cover cursor-pointer" src={"https://www.hubzu.com/www/images/PreviewPDPBanner.jpg"} alt="Click to Add to favorite button, to be notified when lisiting is ready" />
                        </div>
                      }
                      {
                        pdpInitData?.campaignDTO !== null &&
                        <a className="pb20 mt0" href={pdpInitData?.campaignDTO?.campaignUrl === 'CASH-INCENTIVE' ? '/cash-incentive' : pdpInitData?.campaignDTO?.newSlideLink} target="_blank">
                          <img className="img-whp w-100 cover cursor-pointer" src={pdpInitData?.campaignDTO?.campaignImage?.url}  alt="Click to Add to favorite button, to be notified when lisiting is ready" />
                        </a>
                      }

                      <div className="col-md-12 col-lg-12 col-sm-12 pl0">
                        <h4 className="pdpDescriptionTitle" tabIndex="0" id="Description">Description</h4>
                        <PropertyDescriptions />

                      </div>
                       {pdpInitData?.propertyDetails?.legalDescription && (
                      <div className="col-md-12 col-lg-12 col-sm-12 pl0 mt-4">
                        <h4 className="pdpDescriptionTitle" tabIndex="0" id="LegalDescription">Legal Description</h4>
                        <p className="pdpDescriptionPara" style={{ whiteSpace: 'pre-line' }}>
                          {pdpInitData.propertyDetails.legalDescription}
                        </p>
                      </div>
                    )}
                    </div>
                  </div>
                  <div className="row pl0">
                    {pdpInitData &&
                      pdpInitData?.propertyDetails &&
                      pdpInitData?.propertyDetails?.wholeSalerProperty ===
                      "Y" && <AssignmentContract />}
                    {showCryptoBanner && pdpInitData &&
                      pdpInitData?.propertyDetails &&
                      pdpInitData?.propertyDetails?.commonAttributes &&
                      (pdpInitData.propertyDetails.commonAttributes
                        .propCategory == "REO" ||
                        pdpInitData.propertyDetails.commonAttributes
                          .propCategory == "NON_REO") && (
                        <BirdseyBanner
                          bannerDetails={cmsData?.pdpCryptoBanner}
                          propertyId={propertyId}
                          bannerType={"CRYPTO"}
                        />
                      )}
                    {showBirdseyeBanner && propCategory === "COMMERCIAL" && (
                      <BirdseyBanner
                        bannerDetails={cmsData?.pdpBirdsEyeBanner}
                        propertyId={propertyId}
                        bannerType={"BIRDSEYE"}
                      />
                    )}
                    {showSteadilyBanner && <SteadilyBanner />}
<div className="d-block d-sm-none mobile-bid-center mt-3">
  <div className="bid_center_widget p-0 mobile-margin-1">
                  <div className="widget_list">
                    <BidCenter
                      statusPayload={statusPayload}
                      isNDAAccepted={ndaCommercialAccepted}
                      pdpBidCenter={cmsData?.pdpBidCenter}
                      listingType={listingType}
                      status={status}
                      offerType={offerType}
                      offerCenterDetail={offerCenterDetail}
                      isFcOnlineFlag={pdpInitData?.fcOnlineFlag}
                      pdpData={pdpInitData}
                      financialType={financialType}
                      pdpPropertyFees={pdpPropertyFees}
                      isWatchProperty={isWatchProperty}
                      caFcSalePropertyDetails={caFcSalePropertyDetails}
                      isCAFCSaleProperty={isCAFCSaleProperty}
                      data={cmsKeyInfographic}
                    />
                  </div>
                </div>
</div>

                    <div className="mob-pt0 pt30 pl0">
                      {isOccupied && (
                        <div
                          className="pdpGenericContainer mb30"
                          style={{ backgroundColor: "#F0F4F8" }}
                        >
                          <div className="pdp-container container">
                            <div className="row">
                              <div className="col-3 col-lg-1">
                                <img src={isOccupiedImg} alt="inspection" />

                                {/* <img source={Icons.Dash} /> */}
                              </div>
                              <div className="col-9 col-lg-11">
                                <div>
                                  <h4 className="featureTitleOccupied">
                                    {
                                      cmsKeyInfographic.pdpStaticContent[
                                        [propCategory]
                                      ]?.conditionBased?.isOccupied
                                        ?.staticContentHead
                                    }
                                  </h4>
                                  <p className="featureDescription">
                                    {
                                      cmsKeyInfographic.pdpStaticContent[
                                        [propCategory]
                                      ]?.conditionBased?.isOccupied
                                        ?.staticContentBody
                                    }
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {isForeclosure && <ForeclosureSales
                       label={cmsData?.pdpBannerText.foreclosureTxt}
                       ></ForeclosureSales>}
                      {propCategory === "SHRT_SALE" && (
                        <ShortSales
                        label={cmsData?.pdpBannerText.shortSaleTxt}></ShortSales>
                      )}
                      {pdpInitData?.propertyDetails?.sellerId !== "501" &&
                        propCategory === "SC" && <CwcotSales
                        label={cmsData?.pdpBannerText.cwcotTxt}>
                          </CwcotSales>
                        }

                      {propCategory === "COMMERCIAL" ? (
                        <div id="PropertyDetails" className="pl0">
                          <PropertyInfoCommercial
                            ref={sectionRef}
                            pdpData={pdpInitData}
                            pdpPropertyFees={pdpPropertyFees}
                            emd={emd}
                            status={statusPayload}
                            // getbidcenterdata={bidecenterdata}
                            isFcOnlineFlag={pdpInitData?.fcOnlineFlag}
                          />
                        </div>
                      ) : (
                        <div id="PropertyDetails" className="pl0">
                          <PropertyInfo
                            ref={sectionRef}
                            pdpData={pdpInitData}
                            pdpPropertyFees={pdpPropertyFees}
                            emd={emd}
                            status={statusPayload}
                            // getbidcenterdata={bidecenterdata}
                            isFcOnlineFlag={pdpInitData?.fcOnlineFlag}
                          />
                        </div>
                      )}
                    </div>
                    {propCategory === "COMMERCIAL" && <div className="pl0"> <PropertyInfoDetails />  </div>}

                    <div className="pl0" id="InvestmentCalculator">
                      {calculatorFields?.listingprice &&
                        calculatorFields?.listingprice !== "-" &&
                        calculatorFields?.rent_high_mo &&
                        calculatorFields?.rent_low_mo &&
                        calculatorFields?.vacancy_rate &&
                        calculatorFields?.rent_med_mo &&
                        cmsData?.pdpInvestmentCalculator?.enable === "true" && (
                          <InvestmentCalculator isForeclosure={isForeclosure}/>
                        )}
                    </div>

                    {pdpInitData?.propertyDocumentsInformation &&
                      (propCategory === "COMMERCIAL" ? (
                        <div id="Documents" className="pl0">
                          <DocumentSectionCommercial
                            address={address}
                            pdpDocuments={pdpDocuments}
                            propertyDocumentsInformation={
                              pdpInitData?.propertyDocumentsInformation
                            }
                          />
                        </div>
                      ) : null)}
                    <div id="LocalInfo" className="pl0">
                      <LocalInfo
                        taxRecord={taxRecord}
                        priceHistory={priceHistory}
                        demographicInfo={
                          pdpInitData?.propertyDetails?.demographicInformation
                        }
                        propCategory={propCategory}
                        isForeclosure={isForeclosure}
                        propertyData={pdpInitData?.propertyDetails}
                        hidePriceHistory={cmsData?.pdpLocalInfo?.hidePriceHistory}
                        hidePropertyTaxes={cmsData?.pdpLocalInfo?.hidePropertyTaxes}
                      />
                    </div>

                    {/* {pdpInitData?.propertyDocumentsInformation && (
                    <div id="Documents">
                    <DocumentSection
                      address={address}
                      pdpDocuments={pdpDocuments}
                      propertyDocumentsInformation={
                        pdpInitData?.propertyDocumentsInformation
                      }
                    />
                    </div>
                  )} */}

                    <div className="pl0">
                      <Disclaimer
                        data={disclaimer}
                        cms={pdpDisclaimer}
                        pdpData={pdpInitData}
                        provisionalSaleDuration={provisionalSaleDuration}
                      />
                    </div>
                    {pdpInitData?.propertyDocumentsInformation &&
                      (propCategory === "COMMERCIAL" ? null : (
                        <div id="Documents" className="pl0">
                          <DocumentSection
                            address={address}
                            pdpDocuments={pdpDocuments}
                            propertyDocumentsInformation={
                              pdpInitData?.propertyDocumentsInformation
                            }
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              <div className="pdpRight pr0 mobile-pl-0">
                <div className="bid_center_widget p-0 d-none d-sm-block">
                  <div className="widget_list">
                    <BidCenter
                      statusPayload={statusPayload}
                      isNDAAccepted={ndaCommercialAccepted}
                      pdpBidCenter={cmsData?.pdpBidCenter}
                      listingType={listingType}
                      status={status}
                      offerType={offerType}
                      offerCenterDetail={offerCenterDetail}
                      isFcOnlineFlag={pdpInitData?.fcOnlineFlag}
                      pdpData={pdpInitData}
                      financialType={financialType}
                      pdpPropertyFees={pdpPropertyFees}
                      isWatchProperty={isWatchProperty}
                      caFcSalePropertyDetails={caFcSalePropertyDetails}
                      isCAFCSaleProperty={isCAFCSaleProperty}
                      data={cmsKeyInfographic}
                    />
                  </div>
                </div>
                {isForeclosure &&
                  status !== "SPND" &&
                  status !== "OFFMKT" &&
                  pdpInitData?.fcOnlineFlag === "Y" && (
                    <div className="left_side_widget mb0 pb20 pt20">

                      <ForeclosureDetails
                        listingDetails={pdpInitData?.listingDetail}
                      />

                    </div>
                  )}

                {!isCAFCSaleProperty && isForeclosure && (
                  <div className="left_side_widget  mt30">
                    <button
                      onClick={handleForeClosure}
                      className="foreclosure-btn"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        backgroundColor: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        width: "100%",
                        justifyContent: "center",
                      }}
                    >
                      <img src={allFCEvent} alt="All FC Event" />
                      <span
                        style={{
                          fontSize: 16,
                          fontWeight: "bold",
                          color: "#20558B",
                          marginLeft: 10,
                          marginRight: 20,
                        }}
                      >
                        See all Foreclosure Events
                      </span>
                    </button>
                  </div>
                )}

                {!isCAFCSaleProperty && isForeclosure && pdpInitData?.fcOnlineFlag === 'N' && (
                  <div
                    className="left_side_widget mt30 foreclosure-pdp-box"
                  // style={{ paddingTop: 18, paddingBottom: 18 }}
                  >
                    <span
                      style={{
                        fontSize: 16,
                        color: "#000",
                        fontFamily: "graphikregular",
                        lineHeight: "25px"
                      }}
                    >
                      To get more details of other properties as part of this
                      Foreclosure Event.{" "}
                      <span
                        style={{
                          color: "#118FC4",
                          cursor: "pointer"
                        }}
                        onClick={handleForeClosureEvents}
                      >
                        Click here
                      </span>
                    </span>
                  </div>
                )}

                {
                  renderGetAccessCode()
                  // !pdpInitData.propertyDetails.commonAttributes.isOccupied &&
                  // pdpInitData.propertyDetails.commonAttributes.propCategory !== 'SHRT_SALE' &&
                  // pdpInitData.propertyDetails.commonAttributes.propCategory !== 'FORECLOSURE' &&
                  // pdpInitData.propertyDetails.commonAttributes.propCategory !== 'SC' &&
                  // pdpInitData.propertyDetails.commonAttributes?.status !== 'WLST' &&
                  // pdpInitData.propertyDetails.commonAttributes?.status !== 'REVIEW' &&
                  // pdpInitData?.propertySpecialDetails?.lockBoxCode !== '-' &&
                  // !isPreview &&
                  // showGetCode &&
                  // <GetAccessCode />
                }

                {!isCAFCSaleProperty && isForeclosure ? (
                  <AlertRegister
                    cmsData={cmsData?.pdpBidCenter}
                    isForeclosure={isForeclosure}
                    propertyWatchStatus={propertyWatchStatus}
                    propertyId={propertyId}
                    isUserLoggedIn={isUserLoggedIn}
                    key={`alert-register-${isUserLoggedIn}-${propertyWatchStatus}`}
                  />
                ) : !isCAFCSaleProperty ? (
                  <AlertRegister
                    cmsData={cmsData?.pdpBidCenter}
                    isForeclosure={isForeclosure}
                    propertyWatchStatus={propertyWatchStatus}
                    propertyId={propertyId}
                    isUserLoggedIn={isUserLoggedIn}
                    key={`alert-register-${isUserLoggedIn}-${propertyWatchStatus}`}
                  />
                ) : null}

                {isCAFCSaleProperty && caFcSalePropertyDetails ? (
                  <CaseFileDetails data={caFcSalePropertyDetails} />
                ) : hasCaseFileDetails ? (
                  <CaseFileDetails data={pdpInitData?.caseFileDetail} />
                ) : null}

                {/* {hasCaseFileDetails && (
                  <CaseFileDetails data={pdpInitData?.caseFileDetail} />
                )} */}


                <FeaturesSection
                  invrAprvlReqdProp={
                    pdpInitData?.propertySpecialDetails?.invrAprvlReqdProp
                  }
                  financialType={financialType}
                  isForeclosure={isForeclosure}
                  data={cmsKeyInfographic}
                  isFcOnlineFlag={pdpInitData?.fcOnlineFlag}
                  commonAttributes={
                    pdpInitData?.propertyDetails?.commonAttributes
                  }
                  inspectionData={
                    inspectionContigencyDesc ? inspectionContigencyDesc : {}
                  }
                  isPreview={isPreview}
                  listingType={listingType}
                  fcProxyBidEnabled={pdpInitData?.fcProxyBidEnabled}
                  isCAFCSaleProperty={isCAFCSaleProperty}
                // onPressScroll={scrollToComponent}
                />
                {
                  pdpInitData?.propertyDocumentsInformation?.agentDocumentDetailList?.length > 0 &&
                  pdpInitData?.propertyDocumentsInformation?.agentDocumentDetailList?.filter(agent => agent?.email === 'noreply@hubzu.com.test.com')?.length === 0 &&
                   <ContactUsAgents />
                }


                {
                  pdpAds !== null && pdpAds?.length > 0 &&
                  <Advertisment adsData={pdpAds} propertyId={propertyId} />
                }


                {cmsData?.pdpSpringHouseAd?.showAd && (
                  <div className="left_side_widget" id="springHouseAd">
                    <div className="newads">
                      <a
                        href={cmsData?.pdpSpringHouseAd?.imgHref}
                        target="_blank"
                        className="col-lg-12 col-md-12 col-xs-12 col-sm-12"
                        id="springHouseAdClick"
                        onClick={() => {
                          trackButtonClick({
                            eventName: "spring_house_ad_tapped",
                            eventCategory: "Express Capital form",
                            eventSource: "on spring house ad click",
                            eventLabel: `Property Id:${pdpInitData?.propertyDetails?.propertyId} | Screen name : PDP`
                          })
                        }}
                      >
                        <img
                          src={cmsData?.pdpSpringHouseAd?.imgUrl}
                          alt="springhouseAd"
                          target="_blank"
                        />
                      </a>
                      <p className="springHouseDisclaimer" tabIndex="0">
                        {cmsData?.pdpSpringHouseAd?.imgDescLine1}{" "}
                        <a
                          className="afbapdf"
                          href={cmsData?.pdpSpringHouseAd?.imgDescLink}
                          download="AFBA_Disclosure.pdf"
                          aria-label="Download AFBA Disclosure PDF"
                          target="_blank"
                          onClick={() => {
                            trackButtonClick({
                              eventName: "spring_house_ad_doc_tapped",
                              eventCategory: "Express Capital form",
                              eventSource: "on spring house ad click here click",
                              eventLabel: `Property Id: ${pdpInitData?.propertyDetails?.propertyId} | Screen name : PDP`
                            })
                        }} rel="noreferrer"
                        >
                          Download
                        </a>{" "}
                        {cmsData?.pdpSpringHouseAd?.imgDescLine2}
                      </p>
                    </div>
                  </div>
                )}
                {nearbyProperties && nearbyProperties.length > 0 && (
                  <NearbyProperties
                    state={pdpInitData?.propertyDetails?.address?.state}
                    propCategory={
                      pdpInitData?.propertyDetails?.commonAttributes
                        ?.propCategory
                    }
                    nearbyProperties={nearbyProperties}
                    propertyId={propertyId}
                  />
                )}



                {pdpInitData?.listingAgentDetail &&
                  !isForeclosure &&
                  (
                    <ListedBy
                      data={pdpInitData?.listingAgentDetail}
                      cms={pdpListedBy}
                      state={pdpInitData?.propertyDetails?.address?.state}
                    />
                  )}
                {pdpInitData?.coListingAgentDetail && (
                  <ListedBy
                    coList
                    data={mapCoListingAgentDetails(
                      pdpInitData?.coListingAgentDetail
                    )}
                    cms={pdpListedBy}
                  />
                )}
                {hasAuctionFirm && !isTraditional && (
                  <div>
                    {
                    pdpInitData?.listingAgentDetail?.spsSellerFlag === 'Y' ?
                    <AuctionFirmSPS
                      auctioneerDetail={pdpInitData?.listingAgentDetail}
                      address={pdpInitData?.propertyDetails}
                      cms={pdpListedBy}
                      state={pdpInitData?.propertyDetails?.address?.state}
                    />

                    :

                    <AuctionFirm
                      auctioneerDetail={pdpInitData?.listingAgentDetail}
                      address={pdpInitData?.propertyDetails}
                      cms={pdpListedBy}
                      state={pdpInitData?.propertyDetails?.address?.state}
                    />


                  }
                  </div>
                )}
                {hasPSO && (
                  <>
                    <div>
                      <PSO
                        listingAgentDetails={pdpInitData?.listingAgentDetail}
                        psoDetails={pdpInitData?.privateSellingOfficerDto}
                        cms={pdpListedBy}
                        state={pdpInitData?.propertyDetails?.address?.state}
                      />
                    </div>
                  </>
                )}

                {
                  shouldShowFees &&
                   <PropertyFees
                  ref={sectionRef}
                  pdpData={pdpInitData}
                  pdpPropertyFees={pdpPropertyFees}
                  emd={emd}
                  status={statusPayload}
                  getbidcenterdata={bidCenterData}
                  isFcOnlineFlag={pdpInitData?.fcOnlineFlag}
                />
                }

                {pdpInitData?.propertyDetails?.commonAttributes
                  ?.propCategory === "COMMERCIAL" && cmsData?.pdpAds?.ad1?.enable === 'Y' && (
                    <PdpNewAds className="pt20" adsData={cmsData?.pdpAds?.ad1} />
                  )}

                {/* {pdpInitData?.propertySpecialDetails?.premiumTitleMessage?.length > 0 && pdpInitData?.propertySpecialDetails?.displayPtsForPdp === 'N' &&
                  <div style={{ width: 432}}>
                    <div>
                      <a href="" id="pts-popup-trigger" >
                        <img class="img-responsive" src="https://www.hubzu.com/www/images/premium-title-service-new.png" alt="Premium Title Service" />


                      </a>
                    </div>
                  </div>
                } */}
              </div>
            </div>
          </div>
        </section>

        <section className="our-agent-single pb10 pt60 pdp-container container mobile-pdp-resp-buying">
          <div className="p15">
            <div className=" m0">
              <BuyingWithHubzu
                isForeclosure={isForeclosure}
                propertyData={pdpInitData?.propertyDetails}
                isAllowedPreSale={
                  pdpInitData?.propertyDetails?.detailedAttributes
                  ?.isAllowedPreSale
                }
                wholeSalerProperty={
                  pdpInitData?.propertyDetails?.wholeSalerProperty == "Y"
                }
                propCategory={
                  pdpInitData?.propertyDetails?.commonAttributes?.propCategory
                }
                cmsFAQData={cmsData?.pdpBuyingWithHubzu}
              />
              <NeedHelp cmsData={cmsData} propertyData={pdpInitData?.propertyDetails} isForeclosure={isForeclosure}/>
            </div>
          </div>
        </section>
      </div>
    );
  }, [pdpInitData?.propertyDetails?.propertyId, pdpInitData?.propertyDetails?.listingId, cmsData, cmsKeyInfographic, taxRecord, priceHistory, disclaimer, emd, pdpAds, nearbyProperties, propertyWatchStatus, inspectionContigencyDesc  ]);


  return (
    <>
      <PreloadResources />
      {/* <MetaComponent meta={metadata} /> */}
      <Header />
      <MobileMenu />
      <PopupSignInUp />


      {!isContentReady ? (
        <ShimmerLoader />
          // <>

          // <div
          //   className="m30 pt120 pb15"
          //   style={{ display: "flex", flex: 1, justifyContent: "center", alignItems: "center" }}
          // ></div><Loader style={{ paddingTop: 50 }} /></>


      ) : (pdpInitData && (pdpInitData?.propertyDetails === null)) || isWrongURL  ? (
        <div
          className="m30 pt120 pb15"
          style={{ display: "flex", flex: 1, justifyContent: "center", alignItems: "center" }}
        >
          <div className="pdp-container container page_error">
            <div className="propertySoldOutImage">
              <img src="/assets/images/pdp/property-notavailable.png" width="200" height="150" alt="/buy-static/202506301154/www-built//images/property-notavailable.png" />
            </div>
            <span className="page_error_Txt pt20">We're sorry, but this property is currently unavailable.<br />We have plenty more, so look for your next home now.</span>
            <div className="continueYourSearch">
              <a className="continueYourSearch" id="continueSearchByAllStates" href="/auctions?listingType=AUCN" onClick={() => markInternalNavigation()}>CONTINUE YOUR SEARCH</a>
            </div>
          </div>
        </div>
      ) : (
        <>
          <PropertyDetailComp />
          {runTour && (
            <Joyride
              steps={pdpSteps}
              run={runTour}
              continuous={true}
              showSkipButton={false}
              disableScrolling={true}
              spotlightClicks={false}
              beaconComponent={null}
              scrollOffset={100}
              hideBackButton={true}
              scrollToFirstStep={true}
              disableScrollParentFix={true}
              locale={{
                last: "Get Started",
                next: "Next",
              }}
              styles={tourStyles}
              callback={(data) => {
                const { action, index, type } = data;
                if (type === "step:after") {
                  const targetElement = document.querySelector(
                    pdpSteps[index + 1]?.target
                  );
                  if (targetElement) {
                    targetElement.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    });
                  }
                }
                if (data.status === "finished" || data.status === "skipped") {
                  setRunTour(false);
                }
              }}
            />
          )}
        </>
      )}

      {!isPDPLoading && (
          <section id="our-partners" className="our-partners">
            <div className="pdp-container container">
              <div className="row">
                <div className="col-lg-6 offset-lg-3">
                  <div className="main-title text-center">
                  </div>
                </div>
              </div>
              <div className="row">
                <Partners />
              </div>
            </div>
          </section>
      )}

      {!isPDPLoading && (
          // <section className="mt30 mb-0">
            <div className="container-fluid px-0">
                {/* <Footer /> */}
                <OtherLinkFooter/>
                <NewFooter/>
            </div>
          // </section>
      )}
    </>
  );


});

export default PropertyDetails;

